import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ExpenseCategory } from '@/types/financial';
import { useToast } from '@/hooks/use-toast';

export const useExpenseCategories = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['expense-categories'],
    queryFn: async (): Promise<ExpenseCategory[]> => {
      const { data, error } = await supabase
        .from('expense_categories')
        .select('*, dre_structure:dre_structure_id (code)')
        .eq('active', true)
        .order('parent_category_id', { ascending: true, nullsFirst: true })
        .order('category_name');

      if (error) throw error;
      return data || [];
    },
    staleTime: 2 * 60 * 1000, // Cache por 2 minutos
    gcTime: 5 * 60 * 1000,
  });

  const createMutation = useMutation({
    mutationFn: async (category: Partial<ExpenseCategory>) => {
      const { data, error } = await supabase
        .from('expense_categories')
        .insert([category as any])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expense-categories'] });
      toast({ title: 'Categoria de despesa criada' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<ExpenseCategory> & { id: string }) => {
      // Se for soft-delete, usa função RPC dedicada
      if (Object.keys(updates).length === 1 && 'active' in updates && updates.active === false) {
        const { data, error } = await supabase.rpc('soft_delete_expense_category', {
          category_id: id
        });
        
        if (error) {
          console.error('❌ [Expense] Erro no soft-delete:', error);
          throw error;
        }
        
        return data;
      }
      
      // Para outras atualizações, usa UPDATE normal
      const { error, status, statusText } = await supabase
        .from('expense_categories')
        .update(updates)
        .eq('id', id);

      if (error) {
        console.error('❌ [Expense] Erro completo:', JSON.stringify(error, null, 2));
        throw error;
      }
      
      return { id, ...updates };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expense-categories'] });
      toast({ title: 'Categoria de despesa atualizada' });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao atualizar categoria',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    categories: query.data || [],
    isLoading: query.isLoading,
    createCategory: createMutation.mutate,
    updateCategory: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
  };
};
